import { Link } from "react-router-dom";
import { Award, Camera, FileText, Sparkles, Inbox, Trophy, Heart, CalendarDays, Star, Eye, ArrowRight } from "lucide-react";

const BREAKDOWN_META = {
  photos:       { label: "Photos",            icon: Camera,       to: "/talent/portfolio" },
  bio:          { label: "Bio",               icon: FileText,     to: "/talent/profile" },
  universe:     { label: "Univers",           icon: Sparkles,     to: "/talent/universe" },
  applications: { label: "Candidatures",      icon: Inbox,        to: "/collaborations" },
  accepted:     { label: "Collabs acceptées", icon: Star,         to: "/talent/applications" },
  contests:     { label: "Concours",          icon: Trophy,       to: "/talent/concours" },
  votes:        { label: "Votes reçus",       icon: Heart,        to: "/talent/my-contest-entries" },
  events:       { label: "Événements",        icon: CalendarDays, to: "/events" },
  favorites:    { label: "Favoris marques",   icon: Heart,        to: "/talent" },
  views:        { label: "Vues profil",       icon: Eye,          to: "/talent" },
};

/** Gamification card — total points, level tier progress and the breakdown by activity. */
export default function PointsCard({ points = 0, level, breakdown = {} }) {
  const items = Object.entries(breakdown)
    .map(([k, v]) => ({ key: k, value: v, ...(BREAKDOWN_META[k] || { label: k, icon: Award, to: "/talent" }) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
  const pct = level?.progress_pct ?? 0;

  return (
    <div className="rounded-3xl bg-gradient-to-br from-[#FAF3EA] via-white to-[#F5E6D0] border border-[#E6C29A] p-6 sm:p-7" data-testid="points-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="caption-label text-[#A26F3E]">Points Helencia</p>
          <div className="flex items-baseline gap-2 mt-1">
            <span className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] leading-none" data-testid="points-total">{points}</span>
            <span className="text-xs uppercase tracking-wider text-[#8B8680]">pts</span>
          </div>
        </div>
        <div className="text-right">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] text-white text-[10px] uppercase tracking-[0.16em] px-3 py-1.5" data-testid="points-level">
            <Award className="w-3 h-3 text-[#E6C29A]" /> {level?.name || "Débutant"}
          </div>
          {level?.next && (
            <p className="text-[11px] text-[#8B8680] mt-2">
              Encore <span className="font-semibold text-[#1A1A1A]">{Math.max(0, level.next_at - points)}</span> pts → {level.next}
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 h-2 rounded-full bg-white/80 border border-[#EADDC9] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#C68E59] to-[#E6C29A] transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>

      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it) => (
          <Link key={it.key} to={it.to} className="group flex items-center gap-2.5 rounded-2xl bg-white/70 border border-[#EADDC9] px-3 py-2.5 hover:border-[#C68E59]/50 transition" data-testid={`points-${it.key}`}>
            <div className="w-7 h-7 rounded-full bg-[#FAF3EA] flex items-center justify-center shrink-0 group-hover:bg-[#C68E59] transition-colors">
              <it.icon className="w-3.5 h-3.5 text-[#A26F3E] group-hover:text-white transition-colors" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-[#8B8680] truncate">{it.label}</p>
              <p className="text-sm font-semibold text-[#1A1A1A] leading-none mt-0.5">+{it.value}</p>
            </div>
          </Link>
        ))}
      </div>
      <p className="text-[11px] text-[#8B8680] mt-4 inline-flex items-center gap-1">
        Gagnez des points en complétant votre profil, candidatant et participant aux concours <ArrowRight className="w-3 h-3" />
      </p>
    </div>
  );
}
